import React, { useCallback, useState } from 'react';
import {
  useGetVerificationHistoryQuery,
} from '@features/patient-coverages';
import { useUserId } from '@features/user';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { format, parseISO } from 'date-fns';

import { ELIG_TOKENS } from '../theme/readinessTokens';

interface Props {
  patientId?: string;
  encounterId?: string;
  source: string;
}

/**
 * Verification history — appended to the bottom of the eligibility detail
 * drawer. Backed by GET /v2/provider/patients/{id}/eligibility/verifications,
 * which projects manual snapshots into a chronological audit log.
 *
 * Collapsed by default ("3 verifications · Show history ▼") so it's there
 * for trust without cluttering the surface for power users. Empty state
 * renders a single "No verification history" caption with no chrome.
 */
export const VerificationHistorySection: React.FC<Props> = ({
  patientId,
  encounterId,
  source,
}) => {
  const currentUserId = useUserId();
  const { eligibilityChecks } = useEventTracking();
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading } = useGetVerificationHistoryQuery(
    { patientId: patientId ?? '', page: 1, perPage: 50 },
    { skip: !patientId },
  );

  const items = data?.items ?? [];
  const count = items.length;

  const handleToggle = useCallback(() => {
    if (!expanded) {
      eligibilityChecks.verificationHistoryOpened?.({
        patient_id: patientId,
        encounter_id: encounterId,
        source,
        count,
      });
    }
    setExpanded((v) => !v);
  }, [eligibilityChecks, expanded, patientId, encounterId, source, count]);

  if (!patientId) return null;

  if (isLoading) {
    return (
      <Box sx={{ mt: 3 }}>
        <Skeleton variant="text" width={180} height={16} />
      </Box>
    );
  }

  if (count === 0) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
          No verification history
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        component="button"
        type="button"
        onClick={handleToggle}
        sx={{
          background: 'none',
          border: 'none',
          p: 0,
          cursor: 'pointer',
          color: ELIG_TOKENS.inkSoft,
          fontSize: 12,
          textDecoration: 'underline',
          textDecorationThickness: '1px',
          textUnderlineOffset: '2px',
          '&:hover': { color: ELIG_TOKENS.midnight },
        }}
      >
        {count} {count === 1 ? 'verification' : 'verifications'} ·{' '}
        {expanded ? 'Hide history ▲' : 'Show history ▼'}
      </Box>

      {expanded && (
        <Stack spacing={2.5} sx={{ mt: 2 }}>
          {items.map((item) => (
            <VerificationRow
              key={item.eligibility_snapshot_id ?? `${item.created_at ?? ''}-${item.user_id ?? ''}`}
              item={item}
              currentUserId={currentUserId}
            />
          ))}
          {(data?.total ?? 0) > items.length && (
            <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
              Showing the most recent {items.length} of {data?.total ?? items.length}.
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
};

interface VerificationItem {
  changes?: { [key: string]: unknown } | null | undefined;
  created_at?: string | null;
  eligibility_snapshot_id?: string | null;
  notes?: string | null;
  reason?: string | null;
  summary?: string;
  user_id?: string | null;
  user_name?: string | null;
  verification_contacted_at?: string | null;
  verification_method?: string | null;
  verification_reference_number?: string | null;
  verification_rep_name?: string | null;
}

const VerificationRow: React.FC<{
  item: VerificationItem;
  currentUserId: string | undefined;
}> = ({ item, currentUserId }) => {
  const dateLabel = formatDateHeading(
    item.verification_contacted_at ?? item.created_at,
  );
  const actor = composeActor(item, currentUserId);
  const meta = composeMeta(item);
  const changeCount = countChanges(item.changes);
  const noteText = item.notes ?? item.summary ?? null;

  return (
    <Box
      sx={{
        position: 'relative',
        pl: 2,
        borderLeft: `2px solid ${ELIG_TOKENS.line}`,
      }}
    >
      {dateLabel && (
        <Typography
          variant="caption"
          sx={{
            color: ELIG_TOKENS.ink,
            fontWeight: 600,
            fontSize: 11,
            display: 'block',
            mb: 0.25,
          }}
        >
          {dateLabel}
        </Typography>
      )}
      <Typography
        variant="body2"
        sx={{ color: ELIG_TOKENS.inkSoft, fontSize: 13, lineHeight: 1.4 }}
      >
        {[actor, ...meta].filter(Boolean).join(' · ')}
      </Typography>
      {noteText && (
        <Typography
          variant="body2"
          sx={{
            color: ELIG_TOKENS.ink,
            fontSize: 13,
            mt: 0.5,
            fontStyle: 'italic',
            lineHeight: 1.4,
          }}
        >
          “{noteText}”
        </Typography>
      )}
      {changeCount > 0 && (
        <Typography
          variant="caption"
          sx={{ color: ELIG_TOKENS.inkFaint, display: 'block', mt: 0.5 }}
        >
          Updated {changeCount} {changeCount === 1 ? 'field' : 'fields'}
        </Typography>
      )}
    </Box>
  );
};

function formatDateHeading(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const when = parseISO(iso);
  if (Number.isNaN(when.getTime())) return null;
  return format(when, 'EEE, MMM d');
}

function composeActor(
  item: VerificationItem,
  currentUserId: string | undefined,
): string {
  if (item.user_id && currentUserId && item.user_id === currentUserId) {
    return 'You';
  }
  if (item.user_name) return item.user_name;
  if (item.user_id) return 'Another team member';
  return 'System';
}

function composeMeta(item: VerificationItem): string[] {
  const parts: string[] = [];
  if (item.verification_method) parts.push(capitalize(item.verification_method));
  if (item.verification_rep_name) parts.push(item.verification_rep_name);
  if (item.verification_reference_number) {
    parts.push(`ref ${item.verification_reference_number}`);
  }
  return parts;
}


function countChanges(
  changes: { [key: string]: unknown } | null | undefined,
): number {
  if (!changes || typeof changes !== 'object') return 0;
  return Object.keys(changes).length;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
